import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { withStyles, withTheme } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import { withModulesManager, withHistory } from "@openimis/fe-core";
import { fetchAccreditationDetailFormData } from "../actions";

// Defines the JSS (JavaScript in CSS) styles for the component.
// These styles are passed down as a `classes` prop by Material-UI's `withStyles` HOC.
const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
    textAlign: "center",
    width: "100%",
  },
  sectionPaper: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  labelCell: {
    fontWeight: "bold",
    width: "40%",
  },
  subHeaderRow: {
    backgroundColor: theme.palette.action.hover, // A light grey background for sub-headers
  },
  indentedCell: {
    paddingLeft: theme.spacing(4), // Indent nested items
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  emptyMessage: {
    fontStyle: "italic",
    textAlign: "center",
    width: "100%",
    padding: theme.spacing(3),
  },
});
class FormViewer extends Component {
  // Standard React lifecycle method.
  // When the component is first added to the DOM, this function is called.
  componentDidMount() {
    const { uuid, detailType, fetchAccreditationDetailFormData } = this.props;
    // We only fetch data if the necessary IDs are present.
    if (uuid && detailType) {
      fetchAccreditationDetailFormData(uuid, detailType);
    }
  }

  // A simple helper function to format keys for display.
  // It turns `snake_case_keys` into `Title Case Keys`
  formatKey = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  // A visual helper to create a Material-UI Chip component for status fields.
  // It uses different icons and colors based on whether the status is "adequate".
  getStatusChip = (value) => {
    const { classes } = this.props;
    const isAdequate = String(value).includes("functional_adequate");

    return (
      <Chip
        icon={isAdequate ? <CheckCircleIcon /> : <ErrorIcon />}
        label={isAdequate ? "Functional Adequate" : "Non-functional Inadequate"}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  // A boolean check to determine if a value represents a status.
  // This is the core of our "content-aware" logic.
  isStatusField = (value) => {
    if (typeof value === "string") {
      const lowerValue = value.toLowerCase();
      return lowerValue.includes("functional_adequate") || lowerValue.includes("non_functional_inadequate");
    }
    // No need to check for objects here for this logic
    return false;
  };

  // A helper to filter out internal or system-generated keys from the data object
  // so they don't get rendered in the UI.
  shouldSkipKey = (key) => {
    const lowerKey = key.toLowerCase();
    return lowerKey === "__id" || lowerKey === "__system" || lowerKey === "meta";
  };

  // A dispatcher function that decides how to render any given value.
  // If it's a status, it uses the special Chip component. Otherwise, it just converts it to a string.
  renderSimpleValue = (value) => {
    // debug simple render vale

    if (this.isStatusField(value)) return this.getStatusChip(value);
    if (value === null || value === undefined) return "N/A";

    return String(value);
  };

  // A generic, reusable function to render a titled section with a simple key-value table.
  // This is used for both "Details" sections (like Personnel) and "Equipment" sections.
  renderSectionTable = (title, data) => {
    const { classes } = this.props;

    // log and see if we getting an object
    // console.log("Render section", { title, data });
    return (
      <Box mb={3} key={title}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {this.formatKey(title)}
        </Typography>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableBody>
              {Object.entries(data).map(([key, value]) => {
                // Check if the value is a nested object that needs its own sub-section.

                if (typeof value === "object" && (value !== null) & !Array.isArray(value)) {
                  return (
                    <React.Fragment key={key}>
                      <TableRow className={classes.subHeaderRow}>
                        <TableCell colSpan={2}>
                          <Typography variant="subtitle2" className={classes.labelCell}>
                            {this.formatKey(key)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {/* Now, map over the items *inside* the nested object */}
                      {Object.entries(value).map(([subKey, subValue]) => (
                        <TableRow key={subKey}>
                          <TableCell
                            component="th"
                            scope="row"
                            className={`${classes.labelCell} ${classes.indentedCell}`}
                          >
                            {this.formatKey(subKey)}
                          </TableCell>
                          <TableCell>{this.renderSimpleValue(subValue)}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                } else {
                  return (
                    <TableRow key={key}>
                      <TableCell component="th" scope="row" className={classes.tableHeaderCell}>
                        {this.formatKey(key)}
                      </TableCell>
                      <TableCell>{this.renderSimpleValue(value)}</TableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // A dedicated renderer for the final "Results" table.
  // It has special styling (like larger, primary-colored text for scores) to make it stand out.
  renderResultsTable = (data) => {
    const { classes } = this.props;
    if (!data || Object.keys(data).length === 0) return null;
    return (
      <Box mt={4}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Results
        </Typography>
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableBody>
              {Object.entries(data).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell>
                    <Typography variant="body1">{this.formatKey(key)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color="primary">
                      {this.renderSimpleValue(value)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  /**
   * This is the core rendering logic for a single data submission.
   * It intelligently categorizes and then orders the sections from the raw data object.
   
   */
  renderObject = (obj) => {
    // We no longer call organizeData. We do the logic right here.
    if (!obj || typeof obj !== "object") return null;
    console.log("Rendering object:", obj);
    //  separate buckets to hold the different types of sections we find.
    const detailSections = {};
    const equipmentSections = {};
    let resultsSectionData = null;

    // --- STEP 1: Intelligently classify each top-level section based on its content ---
    for (const key in obj) {
      if (this.shouldSkipKey(key)) continue; //Skip system keys
      const value = obj[key];

      // Rule 1: The 'results' object is ALWAYS the final totals section.
      if (key.toLowerCase() === "results") {
        resultsSectionData = value;
        continue;
      }

      // We only care about keys whose values are objects (i.e., sections).
      if (typeof value === "object" && value !== null) {
        // Rule 2: A section is an "Equipment" type if its content includes status fields.
        // We check the values inside the section, not the section's name.
        const isAnEquipmentSection = Object.values(value).some((v) => this.isStatusField(v));

        if (isAnEquipmentSection) {
          equipmentSections[key] = value;
        } else {
          // Rule 3: If it's not Results and not an Equipment type, it must be a "Details" section.
          detailSections[key] = value;
        }
      }
    }

    // log detail section
    // log equipment section

    // --- STEP 2: Render the categorized sections in the desired, hard-coded order ---
    return (
      <Box width="100%">
        {/* Render "Details" sections first (e.g., personnel, non_scoring) */}
        {Object.entries(detailSections).map(([title, data]) => this.renderSectionTable(title, data))}

        {/* Render "Equipment" sections second-to-last (e.g., physical_resources, histophatology) */}
        {Object.entries(equipmentSections).map(([title, data]) => this.renderSectionTable(title, data))}

        {/* Render "Results" section ALWAYS LAST */}
        {this.renderResultsTable(resultsSectionData)}
      </Box>
    );
  };

  // This function cleans and normalizes the incoming data from Redux before it's rendered.
  // It handles various data structures (stringified JSON, nested objects) to ensure
  // the `renderObject` function receives a clean, consistent object.
  processData = (data) => {
    if (!data) return [];
    let processedData = null;
    try {
      if (data.data?.projectDetailFormData?.data) {
        const jsonString = data.data.projectDetailFormData.data;
        processedData = typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
      } else if (Array.isArray(data)) {
        processedData = data;
      } else if (data.data && typeof data.data === "string") {
        processedData = JSON.parse(data.data);
      } else if (data.data && typeof data.data === "object") {
        processedData = data.data;
      } else if (typeof data === "object") {
        processedData = data;
      }

      if (processedData?.value) {
        processedData = processedData.value;
      }

      return processedData && !Array.isArray(processedData) ? [processedData] : processedData || [];
    } catch (error) {
      console.error("Error processing data:", error);
      return [];
    }
  };

  renderSubmission = (data) => {
    try {
      const processedData = this.processData(data);
      if (!processedData || processedData.length === 0) {
        return (
          <Typography variant="body1" color="textSecondary" className={this.props.classes.emptyMessage}>
            No data collected yet
          </Typography>
        );
      }
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" className={this.props.classes.title}>
              Assessment Results
            </Typography>
            <Divider />
          </Grid>
          {processedData.map((item, idx) => (
            <Grid item xs={12} key={idx}>
              <Paper className={this.props.classes.sectionPaper}>{this.renderObject(item)}</Paper>
            </Grid>
          ))}
        </Grid>
      );
    } catch (error) {
      console.error("Error rendering submission:", error);
      return (
        <Typography variant="body1" color="error">
          Error displaying form data. Please try again later.
        </Typography>
      );
    }
  };

  render() {
    const { classes, fetchingAccreditationDetailsFormData, accreditationDetailsFormData } = this.props;

    return (
      <Box className={classes.root}>
        {fetchingAccreditationDetailsFormData ? (
          <CircularProgress />
        ) : (
          this.renderSubmission(accreditationDetailsFormData)
        )}
      </Box>
    );
  }

  // Your propTypes, defaultProps, mapStateToProps, and mapDispatchToProps
  static propTypes = {
    classes: PropTypes.object.isRequired,
    uuid: PropTypes.string,
    detailType: PropTypes.string,
    fetchAccreditationDetailFormData: PropTypes.func.isRequired,
    fetchingAccreditationDetailsFormData: PropTypes.bool,
    accreditationDetailsFormData: PropTypes.object,
  };

  static defaultProps = {
    uuid: "",
    detailType: "",
    fetchingAccreditationDetailsFormData: false,
    accreditationDetailsFormData: {},
  };
}

const mapStateToProps = (state) => ({
  accreditationDetailsFormData: state.quality_assurance_and_accreditation.accreditationDetailsFormData,
  fetchingAccreditationDetailsFormData: state.quality_assurance_and_accreditation.fetchingAccreditationDetailsFormData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ fetchAccreditationDetailFormData }, dispatch);

export default withModulesManager(
  injectIntl(withTheme(withStyles(styles)(withHistory(connect(mapStateToProps, mapDispatchToProps)(FormViewer))))),
);
